import useShowTooltip from 'hooks/useShowTooltip';
import { HorizontalBar } from 'react-chartjs-2';
import { Card, CardHeader, CardBody } from 'reactstrap';
import { textEllipsis } from 'helpers/global';
import useWhiteLabel from 'hooks/useWhiteLabel';

var barColors = [
  '#a5b592',
  '#f3a447',
  '#e7bc29',
  '#d092a7',
  '#9c85c0',
  '#809ec2',
  '#64754f',
  '#ff6600',
  '#937510',
  '#94405d',
  '#92a611'
];

var colors = {
  gray: {
    100: '#f6f9fc',
    200: '#e9ecef',
    300: '#dee2e6',
    400: '#ced4da',
    500: '#adb5bd',
    600: '#8898aa',
    700: '#525f7f',
    800: '#32325d',
    900: '#212529'
  },
  theme: {
    default: '#172b4d',
    primary: '#5e72e4',
    secondary: '#f4f5f7',
    info: '#11cdef',
    success: '#2dce89',
    danger: '#f5365c',
    warning: '#fb6340',
    brand: '#fe5900'
  },
  black: '#12263F',
  white: '#FFFFFF',
  transparent: 'transparent'
};

const AverageValues = ({ isLoading, data: lineGraphData, eyebrow }) => {
  useShowTooltip();

  const baseThickness = 50; // default thickness
  const minThickness = 10; // smallest bar

  const count = lineGraphData?.length ?? 0;

  const reduction = Math.floor(count / 4) * 13;

  const barThickness = Math.max(minThickness, baseThickness - reduction);
  
  const { isWhiteLabelTeam } = useWhiteLabel();

  const localEyebro = eyebrow || (isWhiteLabelTeam ? 'Index Value Graph' : 'GIV Graph');

  const chartAvgValues = {
    options: {
      indexAxis: 'y',
      responsive: true,
      maintainAspectRatio: false,
      legend: {
        display: false,
        position: 'bottom',
        labels: {
          usePointStyle: true,
          padding: 16
        }
      },
      elements: {
        point: {
          radius: 6,
          backgroundColor: colors.theme['brand']
        },
        line: {
          tension: 0.4,
          borderWidth: 4,
          borderColor: colors.theme['brand'],
          backgroundColor: colors.transparent,
          borderCapStyle: 'rounded'
        }
      },
      scales: {
        yAxes: [
          {
            barThickness: barThickness,
            gridLines: {
              color: colors.gray[200],
              zeroLineColor: colors.gray[200]
            },
            ticks: {
              stepSize: 1,
              beginAtZero: true,
              precision: 0,
              display: false
            }
          }
        ],
        xAxes: [
          {
            ticks: {
              stepSize: 1,
              max: 5,
              beginAtZero: true,
              precision: 0
            }
          }
        ]
      },
      layout: {
        padding: {
          top: 5
        }
      }
    },
    data: {
      labels: lineGraphData?.map((elm) => textEllipsis(elm.label, 200)),
      datasets: [
        {
          data: lineGraphData?.map((elm) => elm.value),
          label: 'Dataset',
          backgroundColor: lineGraphData?.map((_, index) => {
            return barColors[index % 11];
          })
        }
      ]
    }
  };

  return (
    <Card className="no-split">
      <CardHeader>
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h6 className="surtitle text-black">{localEyebro}</h6>
            <h5 className="h3 mb-0 text-brand result-survey-question-text">Average Values</h5>
          </div>
        </div>
      </CardHeader>
      <CardBody>
        {isLoading && (
          <div className="h-100 d-flex flex-column justify-content-center">
            <p className="text-center mb-0">Loading...</p>
          </div>
        )}
        {!isLoading && (
          <>
            <div style={{ height: '398px' }}>
              <div className="chart">
                <HorizontalBar
                  data={chartAvgValues.data}
                  options={chartAvgValues.options}
                  className="chart-canvas"
                />
              </div>
            </div>
            <div className="vertical-graph-info">
              {lineGraphData?.map((elm, index) => (
                <div key={index} className="vertical-graph-info__line">
                  <div
                    className="vertical-graph-info__color"
                    style={{
                      backgroundColor: barColors[index % 11]
                    }}></div>
                  <div className="vertical-graph-info__label">
                    {elm.label} ({elm.value})
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </CardBody>
    </Card>
  );
};

export default AverageValues;
